<template>
	<ext-dialog
		v-model="value"
		:title="$t('import_excel')"
		:primaryButtonText="saving ? $t('saving') : $t('save')"
		:secondaryButtonText="saving ? '' : $t('import_excel')"
		@primaryButtonAction="save"
		@secondaryButtonAction="triggerFile"
		@closed="$emit('input', false)"
		dialogType="large"
	>
		<template slot="body">
			<v-row dense justify="center">
				<v-col cols="12" md="6" class="text-center">
					<v-btn
						block
						color="accent"
						outlined
						:href="templateUrl || undefined"
						target="_blank"
						rel="noopener"
						:disabled="!templateUrl"
					>
						<v-icon start class="mr-2">icon-download</v-icon>{{ $t("download_template") }}
					</v-btn>
				</v-col>
			</v-row>

			<input ref="file" type="file" accept=".xlsx,.xls" style="display: none" @change="handleFile" />

			<v-divider class="my-4" />

			<div v-if="!preview.length" class="mb-4">
				{{ $t("select_a_file_to_preview") }}
			</div>

			<v-simple-table v-if="preview.length" dense>
				<thead>
					<tr>
						<th>{{ $t("code") }}</th>
						<th>{{ $t("name") }}</th>
						<th>{{ $t("product_group") }}</th>
						<th>{{ $t("units") }}</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="row in preview" :key="row.code">
						<td>{{ row.code }}</td>
						<td>{{ row.name }}</td>
						<td>{{ row.productGroupName || "–" }}</td>
						<td>{{ row.unitCodes && row.unitCodes.length ? row.unitCodes.join("; ") : "–" }}</td>
					</tr>
				</tbody>
			</v-simple-table>

			<div v-if="payload.length" class="mt-3">
				{{ $t("ready_to_import") }}: {{ stats.products }} {{ $t("products") }}, {{ stats.variants }}
				{{ $t("variants") }}
			</div>
			<div v-if="saving" class="mt-3 d-flex align-center">
				<v-progress-circular indeterminate size="20" class="mr-2" />
				<span>{{ $t("importing_please_wait") }}</span>
			</div>
		</template>
	</ext-dialog>
</template>

<script>
import ExtDialog from "../../../partials/dialogs/ExtDialog.vue";
import * as XLSX from "xlsx";
import remote from "../../../../data/remote";

export default {
	name: "ProductImportDialog",
	components: { ExtDialog },
	props: {
		value: Boolean,
		templateUrl: { type: String, default: "" },
	},
	watch: {
		value(val) {
			if (!val) {
				this.uploading = false;
				this.saving = false;
				this.payload = [];
				this.preview = [];
				this.stats = { products: 0, variants: 0 };
				if (this.$refs.file) this.$refs.file.value = "";
			}
		},
	},

	data() {
		return {
			uploading: false,
			saving: false,
			payload: [],
			preview: [],
			stats: { products: 0, variants: 0 },
			languageMap: {
				Arabic: "ar",
				German: "de",
				English: "en",
				Spanish: "es",
				French: "fr",
				Hindi: "hi",
				Indonesian: "ind",
				Italian: "it",
				Japanese: "ja",
				Korean: "ko",
				Portuguese: "pt",
				Russian: "ru",
				Turkish: "tr",
				Vietnamese: "vi",
				Chinese: "zh",
			},
		};
	},
	computed: {
		defaultLang() {
			return this.$store.getters.companyDefaultLanguage || "en";
		},
	},
	methods: {
		triggerFile() {
			if (this.uploading) return;
			this.$refs.file.click();
		},
		splitList(v) {
			return String(v || "")
				.split(/[;,\n]/)
				.map((s) => s.trim())
				.filter(Boolean);
		},
		coerceBool(v) {
			const s = String(v ?? "")
				.trim()
				.toLowerCase();
			if (!s) return null;
			if (["true", "1", "yes", "evet"].includes(s)) return true;
			if (["false", "0", "no", "hayır", "hayir"].includes(s)) return false;
			return null;
		},
		coerceDate(v) {
			if (v == null || v === "") return null;
			const d = new Date(v);
			return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
		},
		async handleFile(ev) {
			const file = ev.target.files[0];
			if (!file) return;
			this.uploading = true;
			try {
				const data = new Uint8Array(await file.arrayBuffer());
				const wb = XLSX.read(data, { type: "array" });
				const shProducts = wb.Sheets["Products"];
				const shAttrs = wb.Sheets["Attributes"];
				const shOpts = wb.Sheets["AttributeOptions"];
				const shVars = wb.Sheets["Variants"];
				if (!shProducts || !shAttrs || !shOpts || !shVars) throw new Error("missing_sheet");

				const rowsProducts = XLSX.utils.sheet_to_json(shProducts);
				const rowsAttrs = XLSX.utils.sheet_to_json(shAttrs);
				const rowsOpts = XLSX.utils.sheet_to_json(shOpts);
				const rowsVars = XLSX.utils.sheet_to_json(shVars);

				const productIndex = new Map();
				for (const r of rowsProducts) {
					const code = String(r.Product_Code || "").trim();
					if (!code) continue;
					const name = {};
					Object.keys(r).forEach((k) => {
						const m = k.match(/^Product_Name_(.+)$/);
						if (m) {
							const lc = this.languageMap[m[1]] || null;
							if (lc && r[k] && String(r[k]).trim()) name[lc] = String(r[k]).trim();
						}
					});
					if (!name[this.defaultLang]) {
						name[this.defaultLang] = r.Product_Name_English || r.Product_Name_Turkish || "";
					}
					const unitCodes = this.splitList(r.Unit_Codes);
					const hsCodes = this.splitList(r.HS_Codes);
					const images = this.splitList(r.Images);
					const productGroupName = String(r.Product_Group_Name || "").trim() || null;
					productIndex.set(code, {
						code,
						name,
						productGroupName,
						unitCodes,
						hsCodes,
						images,
						attributes: [],
						variants: [],
					});
				}
				if (!productIndex.size) throw new Error("no_valid_products");

				const attrLangCols = Object.keys(rowsAttrs[0] || {}).filter((k) => /^Name_/.test(k));
				const attrDefsByProduct = new Map();
				for (const r of rowsAttrs) {
					const pcode = String(r.Product_Code || "").trim();
					if (!pcode || !productIndex.has(pcode)) continue;
					const type = String(r.Type || "").trim();
					const nameObj = {};
					for (const col of attrLangCols) {
						const disp = col.replace(/^Name_/, "");
						const lc = this.languageMap[disp] || null;
						if (lc && r[col] && String(r[col]).trim()) nameObj[lc] = String(r[col]).trim();
					}
					const canon = nameObj[this.defaultLang] || nameObj.en || Object.values(nameObj)[0] || "";
					if (!canon) continue;
					if (!attrDefsByProduct.has(pcode)) attrDefsByProduct.set(pcode, []);
					attrDefsByProduct.get(pcode).push({ type, name: nameObj, _canonName: canon });
				}

				const optValLangCols = Object.keys(rowsOpts[0] || {}).filter((k) => /^Value_/.test(k));
				for (const r of rowsOpts) {
					const pcode = String(r.Product_Code || "").trim();
					if (!pcode || !attrDefsByProduct.has(pcode)) continue;
					const attrNameCols = Object.keys(r).filter((k) => /^Attribute_Name_/.test(k));
					const incomingAttrName = attrNameCols.map((k) => String(r[k] || "").trim()).find(Boolean);
					if (!incomingAttrName) continue;
					const defs = attrDefsByProduct.get(pcode);
					const target = defs.find((d) => Object.values(d.name || {}).some((nm) => nm === incomingAttrName));
					if (!target) continue;
					const valueObj = {};
					for (const col of optValLangCols) {
						const disp = col.replace(/^Value_/, "");
						const lc = this.languageMap[disp] || null;
						if (lc && r[col] && String(r[col]).trim()) valueObj[lc] = String(r[col]).trim();
					}
					if (!target.options) target.options = [];
					target.options.push({ value: valueObj });
				}

				for (const [code, prod] of productIndex.entries()) {
					const defs = attrDefsByProduct.get(code) || [];
					prod.attributes = defs.map((d) => ({
						type: d.type,
						name: d.name,
						options: Array.isArray(d.options) ? d.options : [],
					}));
				}

				const canonMapByProduct = new Map();
				for (const [code] of productIndex.entries()) {
					const m = new Map();
					const defs = attrDefsByProduct.get(code) || [];
					for (const d of defs) {
						const canon = d._canonName;
						for (const [, nm] of Object.entries(d.name || {})) m.set(nm, canon);
					}
					canonMapByProduct.set(code, m);
				}

				const variantAttrCols = Object.keys(rowsVars[0] || {}).filter((k) => k.startsWith("attr:"));
				for (const r of rowsVars) {
					const pcode = String(r.Product_Code || "").trim();
					if (!pcode || !productIndex.has(pcode)) continue;
					const vcode = String(r.Variant_Code || "").trim();
					if (!vcode) continue;
					const vname = {};
					Object.keys(r).forEach((key) => {
						const m = key.match(/^Variant_Name_(.+)$/);
						if (m) {
							const lc = this.languageMap[m[1]] || null;
							if (lc && r[key] && String(r[key]).trim()) vname[lc] = String(r[key]).trim();
						}
					});
					if (!vname[this.defaultLang]) {
						vname[this.defaultLang] = r.Variant_Name_English || r.Variant_Name_Turkish || "";
					}
					const hasGross = this.coerceBool(r.HasGross) ?? false;
					const canonMap = canonMapByProduct.get(pcode);
					const attrValues = {};
					for (const col of variantAttrCols) {
						const headerName = col.replace(/^attr:/, "").trim();
						const raw = r[col];
						if (raw === undefined || raw === null || String(raw).trim() === "") continue;
						const canon = canonMap.get(headerName) || headerName;
						attrValues[canon] = String(raw).trim();
					}
					productIndex.get(pcode).variants.push({
						code: vcode,
						name: vname,
						hasGross,
						attributes: attrValues,
					});
				}

				const payload = Array.from(productIndex.values()).map((p) => {
					const defs = (attrDefsByProduct.get(p.code) || []).reduce((acc, d) => {
						acc[d._canonName] = d.type;
						return acc;
					}, {});
					const normalizedVariants = (p.variants || []).map((v) => {
						const attrs = {};
						for (const [k, val] of Object.entries(v.attributes || {})) {
							const t = defs[k];
							if (t === "multi_select") attrs[k] = this.splitList(val);
							else if (t === "boolean") attrs[k] = this.coerceBool(val);
							else if (t === "date") attrs[k] = this.coerceDate(val);
							else attrs[k] = val;
						}
						return { code: v.code, name: v.name, hasGross: v.hasGross, attributes: attrs };
					});
					return {
						code: p.code,
						name: p.name,
						productGroupName: p.productGroupName,
						unitCodes: p.unitCodes,
						hsCodes: p.hsCodes,
						images: p.images,
						attributes: p.attributes,
						variants: normalizedVariants,
					};
				});

				this.payload = payload;
				this.stats.products = payload.length;
				this.stats.variants = payload.reduce((n, p) => n + (p.variants?.length || 0), 0);
				this.preview = payload.slice(0, 10).map((p) => ({
					code: p.code,
					name: p.name[this.defaultLang] || p.name.en || "",
					productGroupName: p.productGroupName || "",
					unitCodes: p.unitCodes || [],
				}));
			} catch (e) {
				this.$emit("error", e && e.message ? e.message : String(e));
				this.payload = [];
				this.preview = [];
				this.stats = { products: 0, variants: 0 };
			} finally {
				this.uploading = false;
				ev.target.value = "";
			}
		},
		save() {
			if (!this.payload.length || this.saving) return;
			this.saving = true;
			remote.products.importBulk(this.payload, {
				onSuccess: (res) => {
					this.saving = false;
					this.$emit("saved", res);
					this.$emit("input", false);
				},
				onFail: (err) => {
					this.saving = false;
					const msg = err?.message || String(err);
					this.$emit("error", msg);
				},
			});
		},
	},
};
</script>
